// Connector-type constants and helpers. Keep this file in sync across all
// consumer/admin apps — the CONNECTOR_TYPES array is the source of truth for
// every <select> dropdown, and the helpers drive the icon prefix and the
// kW chip on port cards.
//
// One SVG per connector FAMILY (5 icons for 8 types) — the four Type 2 kW
// variants share the same icon; the shape doesn't change, only the max
// rating. Icons live under /public/connectors/<slug>.svg and are recoloured
// at render time via mask-image + currentColor.

export const CONNECTOR_TYPES = [
  '20A Socket - Max 3.3kW',
  'Type 2 Gun - Max 3.6kW',
  'Type 2 Gun - Max 7.2kW',
  'Type 2 Gun - Max 11kW',
  'Type 2 Gun - Max 22kW',
  'Type 6 Gun - Max 3kW',
  'Type 7 Gun - Max 3kW',
  'CCS 2 Gun - Max 60kW',
] as const;

export type ConnectorType = typeof CONNECTOR_TYPES[number];

/**
 * Server default for any port created without an explicit connector_type.
 * Matches migration 014's ALTER COLUMN … SET DEFAULT.
 */
export const DEFAULT_CONNECTOR: ConnectorType = '20A Socket - Max 3.3kW';

export type ConnectorSlug = '20A_Socket' | 'Type_2' | 'Type_6' | 'Type_7' | 'CCS2';

/** Return the /public/connectors/<slug>.svg slug for a connector type. */
export function connectorIconSlug(name: string | null | undefined): ConnectorSlug | null {
  if (!name) return null;
  if (name.startsWith('20A Socket')) return '20A_Socket';
  if (name.startsWith('Type 2 Gun')) return 'Type_2';
  if (name.startsWith('Type 6 Gun')) return 'Type_6';
  if (name.startsWith('Type 7 Gun')) return 'Type_7';
  if (name.startsWith('CCS 2 Gun'))  return 'CCS2';
  return null;
}

/** Parse the trailing "Max Xkw" out of a connector type. Returns null if unknown. */
export function connectorKw(name: string | null | undefined): number | null {
  if (!name) return null;
  const m = name.match(/Max\s+([\d.]+)kW/i);
  return m ? parseFloat(m[1]) : null;
}

/** Split a connector type into its shape prefix and kW rating. Used to render
 *  the name compactly on port cards ("Type 2" + "7.2 kW"). */
export function connectorShape(name: string | null | undefined): string {
  if (!name) return '';
  const cleaned = name.replace(/\s*-\s*Max.*$/i, '').trim();
  return cleaned || name;
}
